import { useRef, useState, type FormEvent } from 'react';
import {
  api,
  type CatalogPlugin,
  type PluginInput,
  type PluginRisk,
  type PluginVersion,
  type VersionInput,
  type VersionStatus,
} from '../api';
import { formatBytes, formatDateTime } from '../format';
import { useAction, useLoader } from '../hooks';
import { compareSemver, SEMVER_RE } from '../semver';
import { Alert } from './Alert';
import { ConfirmDialog } from './ConfirmDialog';
import { Field } from './Field';
import { RISK_LABELS, RISK_TONES, VERSION_STATUS_LABELS, VERSION_STATUS_TONES } from './labels';
import { Tag } from './Tag';

const SRI_RE = /^sha(256|384|512)-[A-Za-z0-9+/]{43,}={0,2}$/;

const parseList = (text: string): string[] =>
  text
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean);

function isHttps(value: string): boolean {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

/** Dernière version publiée (la plus haute), `null` s'il n'y en a pas. */
export function latestPublished(plugin: CatalogPlugin): PluginVersion | null {
  const published = plugin.versions.filter((v) => v.status === 'published');
  return published.sort((a, b) => compareSemver(b.version, a.version))[0] ?? null;
}

// --- formulaire plugin (création et édition) ------------------------------------------------------------------------

function PluginForm({
  plugin,
  busy,
  error,
  onSubmit,
  onCancel,
}: {
  plugin?: CatalogPlugin;
  busy: boolean;
  error: string | null;
  onSubmit: (input: PluginInput) => void;
  onCancel?: () => void;
}) {
  const [pkg, setPkg] = useState(plugin?.package ?? '');
  const [title, setTitle] = useState(plugin?.title ?? '');
  const [description, setDescription] = useState(plugin?.description ?? '');
  const [risk, setRisk] = useState<PluginRisk>(plugin?.risk ?? 'low');
  const [hasDependencies, setHasDependencies] = useState(plugin?.hasDependencies ?? false);
  const [dependsOn, setDependsOn] = useState((plugin?.dependsOn ?? []).join(', '));
  const [enabledByDefault, setEnabledByDefault] = useState(plugin?.enabledByDefault ?? false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function submit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!plugin && !/^(@[a-z0-9][a-z0-9._~-]*\/)?[a-z0-9][a-z0-9._~-]*$/.test(pkg.trim()))
      next.package = 'Nom de paquet npm invalide (minuscules, ex. @weaverize/wvrz-kanban).';
    if (!title.trim()) next.title = 'Le titre est obligatoire.';
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit({
      ...(plugin ? {} : { package: pkg.trim() }),
      title: title.trim(),
      description: description.trim(),
      risk,
      hasDependencies,
      dependsOn: parseList(dependsOn),
      enabledByDefault,
    } as PluginInput);
  }

  return (
    <form className="form-grid" onSubmit={submit} noValidate>
      {!plugin && (
        <Field label="Paquet npm" hint="Nom exact du paquet, non modifiable ensuite." error={errors.package}>
          {(p) => (
            <input
              {...p}
              type="text"
              value={pkg}
              autoComplete="off"
              autoCapitalize="none"
              spellCheck={false}
              onChange={(e) => setPkg(e.target.value)}
            />
          )}
        </Field>
      )}
      <Field label="Titre" error={errors.title}>
        {(p) => <input {...p} type="text" value={title} onChange={(e) => setTitle(e.target.value)} />}
      </Field>
      <Field label="Risque">
        {(p) => (
          <select {...p} value={risk} onChange={(e) => setRisk(e.target.value as PluginRisk)}>
            {(Object.keys(RISK_LABELS) as PluginRisk[]).map((r) => (
              <option key={r} value={r}>
                {RISK_LABELS[r]}
              </option>
            ))}
          </select>
        )}
      </Field>
      <Field label="Description">
        {(p) => (
          <textarea {...p} rows={3} value={description} onChange={(e) => setDescription(e.target.value)} />
        )}
      </Field>
      <Field label="Dépend de" hint="Paquets du catalogue, séparés par des virgules ou des espaces.">
        {(p) => (
          <input
            {...p}
            type="text"
            value={dependsOn}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setDependsOn(e.target.value)}
          />
        )}
      </Field>
      <div className="field">
        <label className="check">
          <input
            type="checkbox"
            checked={hasDependencies}
            onChange={(e) => setHasDependencies(e.target.checked)}
          />
          Le paquet a des <code>dependencies</code> (installation en <code>file:</code>)
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={enabledByDefault}
            onChange={(e) => setEnabledByDefault(e.target.checked)}
          />
          Activé par défaut sur toutes les instances
        </label>
      </div>
      {error && <Alert>{error}</Alert>}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          {plugin ? 'Enregistrer' : 'Ajouter au catalogue'}
        </button>
        {onCancel && (
          <button type="button" className="btn" onClick={onCancel}>
            Annuler
          </button>
        )}
      </div>
    </form>
  );
}

// --- versions -------------------------------------------------------------------------------------------------------

/** Origine d'une version : registre npm, archive téléversée dans WHCC, ou archive externe (url + empreinte). */
type VersionKind = 'registry' | 'hosted' | 'external';

function VersionForm({ busy, onSubmit }: { busy: boolean; onSubmit: (v: VersionInput) => void }) {
  const [version, setVersion] = useState('');
  const [kind, setKind] = useState<VersionKind>('registry');
  const [integrity, setIntegrity] = useState('');
  const [url, setUrl] = useState('');
  const [status, setStatus] = useState<VersionStatus>('draft');
  const [errors, setErrors] = useState<Record<string, string>>({});

  function submit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    if (!SEMVER_RE.test(version.trim())) next.version = 'Version exacte attendue (x.y.z), pas une plage.';
    if (kind !== 'hosted' && !SRI_RE.test(integrity.trim()))
      next.integrity = 'Empreinte SRI attendue : sha512-<base64>.';
    if (kind === 'external' && !isHttps(url.trim()))
      next.url = "URL https de l'archive obligatoire pour une archive externe.";
    setErrors(next);
    if (Object.keys(next).length > 0) return;
    onSubmit(
      kind === 'hosted'
        ? // Aucune empreinte ni url : posées par le serveur au téléversement ; la version reste en brouillon jusque-là.
          { version: version.trim(), source: 'tarball' }
        : {
            version: version.trim(),
            source: kind === 'registry' ? 'registry' : 'tarball',
            integrity: integrity.trim(),
            ...(kind === 'external' ? { url: url.trim() } : {}),
            status,
          },
    );
    setVersion('');
    setIntegrity('');
    setUrl('');
  }

  return (
    <form className="form-grid" onSubmit={submit} noValidate aria-label="Ajouter une version">
      <Field label="Version" error={errors.version}>
        {(p) => (
          <input
            {...p}
            type="text"
            value={version}
            placeholder="1.2.3"
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => setVersion(e.target.value)}
          />
        )}
      </Field>
      <Field
        label="Source"
        hint={
          kind === 'hosted'
            ? "Créez la version, puis téléversez l'archive (npm pack) depuis la liste des versions : WHCC la contrôle et calcule son empreinte."
            : undefined
        }
      >
        {(p) => (
          <select {...p} value={kind} onChange={(e) => setKind(e.target.value as VersionKind)}>
            <option value="registry">Registre npm</option>
            <option value="hosted">Archive hébergée par WHCC (téléversée)</option>
            <option value="external">Archive externe (url + empreinte)</option>
          </select>
        )}
      </Field>
      {kind !== 'hosted' && (
        <Field
          label="Empreinte (intégrité)"
          hint="SRI sha512 de l'archive ou du paquet publié."
          error={errors.integrity}
        >
          {(p) => (
            <input
              {...p}
              type="text"
              value={integrity}
              placeholder="sha512-…"
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => setIntegrity(e.target.value)}
            />
          )}
        </Field>
      )}
      {kind === 'external' && (
        <Field
          label="URL de l'archive"
          hint="https uniquement ; l'empreinte est vérifiée avant installation."
          error={errors.url}
        >
          {(p) => (
            <input
              {...p}
              type="text"
              value={url}
              placeholder="https://…/paquet-1.2.3.tgz"
              autoComplete="off"
              spellCheck={false}
              onChange={(e) => setUrl(e.target.value)}
            />
          )}
        </Field>
      )}
      {kind !== 'hosted' && (
        <Field label="Statut initial" hint="Un brouillon n'est servi qu'aux instances qui l'épinglent.">
          {(p) => (
            <select {...p} value={status} onChange={(e) => setStatus(e.target.value as VersionStatus)}>
              <option value="draft">Brouillon</option>
              <option value="published">Publiée</option>
            </select>
          )}
        </Field>
      )}
      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={busy}>
          Ajouter la version
        </button>
      </div>
    </form>
  );
}

/** Une archive se téléverse tant que la version est un brouillon `tarball` sans archive externe. */
const acceptsUpload = (v: PluginVersion): boolean =>
  v.source === 'tarball' && v.status === 'draft' && (v.hosted === true || !v.url);

/** Sélecteur de fichier .tgz (étiquette accessible portant la version) ; l'envoi part dès le choix du fichier. */
function ArtifactPicker({
  version,
  replace,
  busy,
  onPick,
}: {
  version: string;
  replace: boolean;
  busy: boolean;
  onPick: (file: File) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <label className={`btn btn-small${busy ? ' is-disabled' : ''}`}>
      {replace ? "Remplacer l'archive" : "Téléverser l'archive"} <span className="sr-only">{version}</span>
      <input
        ref={input}
        type="file"
        className="sr-only"
        accept=".tgz,.tar.gz,application/gzip"
        disabled={busy}
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Remise à zéro : choisir deux fois le même fichier (après une correction) relance l'envoi.
          if (input.current) input.current.value = '';
          if (file) onPick(file);
        }}
      />
    </label>
  );
}

type PendingVersion = { version: PluginVersion; kind: 'withdraw' | 'delete' | 'delete-artifact' };

function PluginSheet({
  plugin,
  onChanged,
  onDeleted,
}: {
  plugin: CatalogPlugin;
  onChanged: () => Promise<void>;
  onDeleted: () => Promise<void>;
}) {
  const edit = useAction();
  const versionAction = useAction();
  const del = useAction();
  const [editing, setEditing] = useState(false);
  const [pending, setPending] = useState<PendingVersion | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [typed, setTyped] = useState('');
  const versions = [...plugin.versions].sort((a, b) => compareSemver(b.version, a.version));

  async function save(input: PluginInput) {
    const { package: _package, ...patch } = input;
    if ((await edit.run(() => api.updatePlugin(plugin.id, patch))) !== undefined) {
      setEditing(false);
      await onChanged();
    }
  }

  async function setStatus(v: PluginVersion, status: VersionStatus) {
    if (
      (await versionAction.run(() => api.setPluginVersionStatus(plugin.id, v.version, status))) !== undefined
    )
      await onChanged();
    setPending(null);
  }

  async function removeVersion(v: PluginVersion) {
    const r = await versionAction.run(async () => {
      await api.deletePluginVersion(plugin.id, v.version);
      return true;
    });
    if (r) await onChanged();
    setPending(null);
  }

  async function uploadArtifact(v: PluginVersion, file: File) {
    if ((await versionAction.run(() => api.uploadPluginArtifact(plugin.id, v.version, file))) !== undefined)
      await onChanged();
  }

  async function removeArtifact(v: PluginVersion) {
    if ((await versionAction.run(() => api.deletePluginArtifact(plugin.id, v.version))) !== undefined)
      await onChanged();
    setPending(null);
  }

  async function removePlugin() {
    const r = await del.run(async () => {
      await api.deletePlugin(plugin.id);
      return true;
    });
    if (r) await onDeleted();
    setConfirmDelete(false);
    setTyped('');
  }

  return (
    <section className="panel stack" aria-labelledby={`plugin-${plugin.id}`}>
      <div className="page-head">
        <h2 id={`plugin-${plugin.id}`}>
          {plugin.title} <code>{plugin.package}</code>
        </h2>
        <div className="form-actions">
          <button type="button" className="btn btn-small" onClick={() => setEditing((v) => !v)}>
            {editing ? 'Fermer la modification' : 'Modifier la fiche'}
          </button>
          <button
            type="button"
            className="btn btn-small btn-danger-outline"
            onClick={() => {
              setTyped('');
              setConfirmDelete(true);
            }}
          >
            Supprimer le plugin
          </button>
        </div>
      </div>

      {del.error && <Alert>{del.error}</Alert>}

      {editing ? (
        <PluginForm
          plugin={plugin}
          busy={edit.busy}
          error={edit.error}
          onSubmit={(i) => void save(i)}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <dl className="meta">
          <div>
            <dt>Risque</dt>
            <dd>
              <Tag tone={RISK_TONES[plugin.risk]}>{RISK_LABELS[plugin.risk]}</Tag>
            </dd>
          </div>
          <div>
            <dt>Activé par défaut</dt>
            <dd>{plugin.enabledByDefault ? 'Oui' : 'Non'}</dd>
          </div>
          <div>
            <dt>Dépendances npm</dt>
            <dd>{plugin.hasDependencies ? 'Oui (installation en file:)' : 'Non'}</dd>
          </div>
          <div>
            <dt>Dépend de</dt>
            <dd>
              {plugin.dependsOn.length > 0 ? (
                plugin.dependsOn.map((d) => (
                  <code key={d} className="chip">
                    {d}
                  </code>
                ))
              ) : (
                <span className="muted">aucun</span>
              )}
            </dd>
          </div>
          <div className="meta-wide">
            <dt>Description</dt>
            <dd>{plugin.description || <span className="muted">—</span>}</dd>
          </div>
        </dl>
      )}

      <h3>Versions</h3>
      <p className="muted">
        Une archive hébergée se téléverse tant que la version est un brouillon (fichier <code>.tgz</code>{' '}
        produit par <code>npm pack</code>, sans script d'installation). Une fois la version publiée, l'archive
        est immuable.
      </p>
      {versionAction.busy && <p role="status">Envoi en cours…</p>}
      {versionAction.error && <Alert>{versionAction.error}</Alert>}
      {versions.length === 0 ? (
        <p className="muted">Aucune version : le plugin ne peut être servi à aucune instance.</p>
      ) : (
        <div className="table-wrap">
          <table>
            <caption className="sr-only">Versions de {plugin.package}</caption>
            <thead>
              <tr>
                <th scope="col">Version</th>
                <th scope="col">Statut</th>
                <th scope="col">Source</th>
                <th scope="col">Empreinte</th>
                <th scope="col">Ajoutée</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id}>
                  <th scope="row">{v.version}</th>
                  <td>
                    <Tag tone={VERSION_STATUS_TONES[v.status]}>{VERSION_STATUS_LABELS[v.status]}</Tag>
                  </td>
                  <td>
                    {v.source === 'registry'
                      ? 'Registre npm'
                      : v.hosted || !v.url
                        ? 'Archive hébergée'
                        : 'Archive externe'}
                    {v.hosted && <span className="muted"> · {formatBytes(v.sizeBytes)}</span>}
                    {v.source === 'tarball' && !v.url && (
                      <div>
                        <Tag tone="orange">En attente de téléversement</Tag>
                      </div>
                    )}
                    {v.url && (
                      <div className="url muted" title={v.url}>
                        {v.url}
                      </div>
                    )}
                  </td>
                  <td>
                    {v.integrity ? (
                      <code className="details" title={v.integrity}>
                        {v.integrity.length > 28 ? `${v.integrity.slice(0, 28)}…` : v.integrity}
                      </code>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className="nowrap">{formatDateTime(v.createdAt)}</td>
                  <td>
                    <div className="row-actions">
                      {acceptsUpload(v) && (
                        <ArtifactPicker
                          version={v.version}
                          replace={v.hosted === true}
                          busy={versionAction.busy}
                          onPick={(file) => void uploadArtifact(v, file)}
                        />
                      )}
                      {acceptsUpload(v) && v.hosted && (
                        <button
                          type="button"
                          className="btn btn-small"
                          disabled={versionAction.busy}
                          onClick={() => setPending({ version: v, kind: 'delete-artifact' })}
                        >
                          Retirer l'archive <span className="sr-only">{v.version}</span>
                        </button>
                      )}
                      {v.status === 'draft' && (
                        <button
                          type="button"
                          className="btn btn-small"
                          disabled={versionAction.busy || (v.source === 'tarball' && !v.url)}
                          title={
                            v.source === 'tarball' && !v.url
                              ? "Téléversez l'archive avant de publier"
                              : undefined
                          }
                          onClick={() => void setStatus(v, 'published')}
                        >
                          Publier <span className="sr-only">{v.version}</span>
                        </button>
                      )}
                      {v.status === 'published' && (
                        <button
                          type="button"
                          className="btn btn-small"
                          disabled={versionAction.busy}
                          onClick={() => setPending({ version: v, kind: 'withdraw' })}
                        >
                          Retirer <span className="sr-only">la version {v.version}</span>
                        </button>
                      )}
                      {v.status === 'withdrawn' && !v.hosted && (
                        <button
                          type="button"
                          className="btn btn-small"
                          disabled={versionAction.busy}
                          onClick={() => void setStatus(v, 'draft')}
                        >
                          Remettre en brouillon <span className="sr-only">{v.version}</span>
                        </button>
                      )}
                      <button
                        type="button"
                        className="btn btn-small btn-danger-outline"
                        disabled={versionAction.busy}
                        onClick={() => setPending({ version: v, kind: 'delete' })}
                      >
                        Supprimer <span className="sr-only">la version {v.version}</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <details className="disclosure">
        <summary>Ajouter une version</summary>
        <VersionForm
          busy={versionAction.busy}
          onSubmit={(v) =>
            void versionAction
              .run(() => api.addPluginVersion(plugin.id, v))
              .then((r) => (r !== undefined ? onChanged() : undefined))
          }
        />
      </details>

      {pending?.kind === 'withdraw' && (
        <ConfirmDialog
          title="Retirer la version"
          confirmLabel="Retirer la version"
          danger
          busy={versionAction.busy}
          onConfirm={() => void setStatus(pending.version, 'withdrawn')}
          onCancel={() => setPending(null)}
        >
          <p>
            La version <strong>{pending.version.version}</strong> de <code>{plugin.package}</code> ne sera
            plus proposée : les instances qui l'épinglent ne pourront plus la recevoir.
          </p>
        </ConfirmDialog>
      )}
      {pending?.kind === 'delete-artifact' && (
        <ConfirmDialog
          title="Retirer l'archive"
          confirmLabel="Retirer l'archive"
          danger
          busy={versionAction.busy}
          onConfirm={() => void removeArtifact(pending.version)}
          onCancel={() => setPending(null)}
        >
          <p>
            L'archive de la version <strong>{pending.version.version}</strong> de{' '}
            <code>{plugin.package}</code> sera supprimée de WHCC : la version redevient « en attente de
            téléversement » et n'est plus servie.
          </p>
        </ConfirmDialog>
      )}
      {pending?.kind === 'delete' && (
        <ConfirmDialog
          title="Supprimer la version"
          confirmLabel="Supprimer la version"
          danger
          busy={versionAction.busy}
          onConfirm={() => void removeVersion(pending.version)}
          onCancel={() => setPending(null)}
        >
          <p>
            Supprimer définitivement la version <strong>{pending.version.version}</strong> de{' '}
            <code>{plugin.package}</code> ? Le serveur la refuse si une instance l'épingle.
          </p>
        </ConfirmDialog>
      )}
      {confirmDelete && (
        <ConfirmDialog
          title="Supprimer le plugin"
          confirmLabel="Supprimer le plugin"
          danger
          busy={del.busy}
          confirmDisabled={typed !== plugin.package}
          onConfirm={() => void removePlugin()}
          onCancel={() => setConfirmDelete(false)}
        >
          <p>
            Le plugin <strong>{plugin.package}</strong> et toutes ses versions seront retirés du catalogue.
          </p>
          <Field label={`Retapez ${plugin.package} pour confirmer`}>
            {(p) => (
              <input
                {...p}
                type="text"
                value={typed}
                autoFocus
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => setTyped(e.target.value)}
              />
            )}
          </Field>
        </ConfirmDialog>
      )}
    </section>
  );
}

// --- onglet ---------------------------------------------------------------------------------------------------------

export function PluginsTab() {
  const { data: plugins, error, reload } = useLoader(api.plugins);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const create = useAction();
  const selected = plugins?.find((p) => p.id === selectedId) ?? null;

  async function add(input: PluginInput) {
    const plugin = await create.run(() => api.createPlugin(input));
    if (plugin) {
      setCreating(false);
      setSelectedId(plugin.id);
      await reload();
    }
  }

  return (
    <div className="stack">
      <div className="page-head">
        <p className="muted">
          Catalogue des plugins que WHCC peut installer sur les instances. Un plugin n'est servi qu'avec une
          version publiée (ou épinglée) ; l'agent est livré par l'image et n'apparaît pas ici.
        </p>
        <button type="button" className="btn btn-primary" onClick={() => setCreating((v) => !v)}>
          {creating ? 'Annuler' : 'Nouveau plugin'}
        </button>
      </div>

      {creating && (
        <section className="panel" aria-label="Nouveau plugin">
          <h2>Nouveau plugin</h2>
          <PluginForm busy={create.busy} error={create.error} onSubmit={(i) => void add(i)} />
        </section>
      )}

      {error && <Alert>{error}</Alert>}
      {plugins === null && !error && (
        <p className="muted" aria-busy="true">
          Chargement du catalogue…
        </p>
      )}
      {plugins?.length === 0 && (
        <div className="empty">
          <p>Le catalogue de plugins est vide.</p>
          <p className="muted">
            Ajoutez un plugin, puis une version publiée, pour pouvoir l'attribuer à une instance.
          </p>
        </div>
      )}
      {plugins && plugins.length > 0 && (
        <div className="table-wrap">
          <table>
            <caption className="sr-only">Catalogue de plugins</caption>
            <thead>
              <tr>
                <th scope="col">Plugin</th>
                <th scope="col">Risque</th>
                <th scope="col">Dernière publiée</th>
                <th scope="col">Versions</th>
                <th scope="col">Par défaut</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {plugins.map((p) => {
                const latest = latestPublished(p);
                return (
                  <tr key={p.id} className={p.id === selectedId ? 'row-selected' : undefined}>
                    <th scope="row" className="user-cell">
                      <div>{p.title}</div>
                      <div className="muted">
                        <code>{p.package}</code>
                      </div>
                    </th>
                    <td>
                      <Tag tone={RISK_TONES[p.risk]}>{RISK_LABELS[p.risk]}</Tag>
                    </td>
                    <td>{latest ? latest.version : <span className="muted">aucune</span>}</td>
                    <td>{p.versions.length}</td>
                    <td>{p.enabledByDefault ? 'Activé' : 'Non'}</td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-small"
                        aria-pressed={p.id === selectedId}
                        onClick={() => setSelectedId(p.id === selectedId ? null : p.id)}
                      >
                        {p.id === selectedId ? 'Masquer' : 'Fiche'}{' '}
                        <span className="sr-only">de {p.package}</span>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selected && (
        <PluginSheet
          key={selected.id}
          plugin={selected}
          onChanged={reload}
          onDeleted={async () => {
            setSelectedId(null);
            await reload();
          }}
        />
      )}
    </div>
  );
}
